import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useTrainState, { ITrain, ITrainSkillPayload } from '@/states/train.state';
import useTenantState from '@/states/tenant.state';
import useOrgChartState from '@/states/organization.state';
import { useGetOrgChartNames } from '@/pages/company-profile/sop/useSopMedia';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useRefetchCompletionStatus } from './useCompletionStatus';
import type { TrainMatrixRow } from '@/components/company/train/TrainMatrixTable';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const useTrainLogic = () => {
  const refetchCompletionStatus = useRefetchCompletionStatus();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms debounce
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const { trains, paginatedData, getTrains, createTrain, updateTrain, deleteTrain, bulkPublishTrain, isLoading } =
    useTrainState();
  const { currentTenant } = useTenantState();
  const { orgCharts, fetchOrgCharts } = useOrgChartState();
  // Role dropdown must list every jabatan, not just those wired into the
  // org-chart tree, so source it from the dedicated names endpoint. This
  // endpoint also returns the hierarchy-sorted ordering used for the matrix.
  const { data: orgChartNames = [] } = useGetOrgChartNames(currentTenant?.id, {
    order_by: 'org_level',
    order: 'asc',
  });

  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<ITrain | null>(null);
  const [allPublished, setAllPublished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'org_level',
    order: 'asc',
  });

  useEffect(() => {
    setAllPublished(trains.length > 0 && trains.every(item => item.is_published));
  }, [trains]);

  // Pull the full org-chart list (with division/level metadata) once so the
  // matrix can render a row for every jabatan, even those without train data.
  useEffect(() => {
    if (currentTenant?.id) fetchOrgCharts(currentTenant.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id]);

  // Fetch every train record (no server-side pagination) so we can merge each
  // role's existing skills/culture/knowledge into its matrix row.
  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      await getTrains(currentTenant.id, {
        page: 0,
        page_size: 1000,
        order: pageConfig.order,
        order_by: pageConfig.orderBy,
      });
    } catch {
      toast.error('Failed to fetch data');
    }
  }, [currentTenant?.id, pageConfig.order, pageConfig.orderBy, getTrains]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Data now comes from server-side pagination
  const displayData = paginatedData || {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 10,
    number: 0,
  };

  // Source the role list for the filter dropdown. Prefer the hierarchy-sorted
  // names endpoint; fall back to the org-chart list.
  const roleSource = orgChartNames.length > 0 ? orgChartNames : orgCharts;
  const roleOptions = roleSource.map(node => ({ label: node.name, value: String(node.id) }));

  // Build one matrix row per jabatan, merging any existing train record.
  const matrixRows: TrainMatrixRow[] = useMemo(() => {
    const trainByNode = new Map<number, ITrain>();
    for (const t of trains) {
      if (t.org_chart_node_id != null) trainByNode.set(t.org_chart_node_id, t);
    }
    return roleSource.map(node => ({
      org_chart_node_id: node.id,
      org_chart_node_name: node.name,
      train: trainByNode.get(node.id),
    }));
  }, [roleSource, trains]);

  // Client-side filter by search term + selected jabatan.
  const filteredMatrixRows = useMemo(() => {
    return matrixRows.filter(row => {
      const matchSearch =
        !debouncedSearchTerm || row.org_chart_node_name.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      const matchRole = !selectedRole || String(row.org_chart_node_id) === selectedRole;
      return matchSearch && matchRole;
    });
  }, [matrixRows, debouncedSearchTerm, selectedRole]);

  const handleSave = async (payload: ITrainSkillPayload) => {
    if (!currentTenant?.id) return;
    try {
      if (formMode === 'edit' && selectedRow?.id) {
        await updateTrain(currentTenant.id, selectedRow.id, payload);
      } else {
        await createTrain(currentTenant.id, payload);
      }
      setFormMode(null);
      setSelectedRow(null);
      await fetchData();
      await refetchCompletionStatus();
    } catch {
      toast.error('Failed to save training');
    }
  };

  // Persist every row that carries at least one skill. Existing roles are
  // updated; new ones are created. Returns true on full success.
  const saveAll = async (payloads: ITrainSkillPayload[]): Promise<boolean> => {
    if (!currentTenant?.id) return false;
    if (payloads.length === 0) {
      toast.error('Minimal 1 skill wajib diisi untuk salah satu jabatan');
      return false;
    }
    const existingByNode = new Map<number, ITrain>();
    for (const t of trains) {
      if (t.org_chart_node_id != null) existingByNode.set(t.org_chart_node_id, t);
    }
    setIsSaving(true);
    try {
      for (const payload of payloads) {
        const existing = existingByNode.get(payload.org_chart_node_id);
        if (existing?.id) {
          await updateTrain(currentTenant.id, existing.id, payload);
        } else {
          await createTrain(currentTenant.id, payload);
        }
      }
      await fetchData();
      await refetchCompletionStatus();
      return true;
    } catch {
      toast.error('Gagal menyimpan Train the Trainer');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (row: ITrain) => {
    // Row-level delete must wipe the whole role. row.id seeds the
    // grouped response (first record's id) which the old delete used
    // to remove only that single record — leaving the rest of the
    // role's attitude/skill/knowledge rows intact. Use the role id
    // (org_chart_node_id) so the BE cascade-delete clears them all.
    if (!currentTenant?.id || row.org_chart_node_id == null) return;
    await deleteTrain(currentTenant.id, row.org_chart_node_id);
    await fetchData();
    await refetchCompletionStatus();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRole('');
    setSelectedStatus('');
    setPageConfig(prev => ({ ...prev, page: 0 }));
  };

  return {
    formMode,
    setFormMode,
    selectedRow,
    setSelectedRow,
    allPublished,
    isLoading,
    isSaving,
    displayData,
    matrixRows: filteredMatrixRows,
    handleSave,
    saveAll,
    handleDelete,
    bulkPublishTrain,
    searchTerm,
    setSearchTerm,
    selectedRole,
    setSelectedRole,
    selectedCategory,
    setSelectedCategory,
    selectedStatus,
    setSelectedStatus,
    pageConfig,
    setPageConfig,
    currentTenant,
    fetchData,
    clearFilters,
    roleOptions,
    orgCharts,
  };
};
